import React from 'react';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { ICoachTeam } from '../../../../models/m6-2';

interface Props {
  detectedTeamFromFile: string;
  currentTeamId: string;
  currentTeamName: string;
  coachTeams: ICoachTeam[];
  selectedTargetTeamId: string;
  onChangeTargetTeam: (teamId: string) => void;
}

type Mode = 'current' | 'detected' | 'other';

/**
 * Sezione Step 3 wizard quando file dichiara team diverso dal context (M6.2 B10).
 * Radio 3 opzioni:
 *  1. Salva nel team corrente (default)
 *  2. Cambia contesto al team rilevato (se coach ha accesso)
 *  3. Salva in altro team di cui sono coach (dropdown)
 */
export function CrossTeamDestinationSection({
  detectedTeamFromFile,
  currentTeamId,
  currentTeamName,
  coachTeams,
  selectedTargetTeamId,
  onChangeTargetTeam,
}: Props) {
  const detectedTeam = coachTeams.find(
    (t) =>
      t.name.toLowerCase().trim() ===
      detectedTeamFromFile.toLowerCase().trim(),
  );

  const otherTeams = coachTeams.filter(
    (t) => t._id !== currentTeamId && t._id !== detectedTeam?._id,
  );

  const [mode, setMode] = React.useState<Mode>('current');

  React.useEffect(() => {
    if (mode === 'current') onChangeTargetTeam(currentTeamId);
    if (mode === 'detected' && detectedTeam) {
      onChangeTargetTeam(detectedTeam._id);
    }
  }, [mode, currentTeamId, detectedTeam, onChangeTargetTeam]);

  return (
    <Box
      borderWidth={1}
      borderColor="blue.300"
      borderRadius="md"
      bg="blue.50"
      p={4}
      mb={3}
    >
      <VStack align="stretch" gap={3}>
        <Text fontWeight="semibold" color="blue.800">
          Team rilevato dal file
        </Text>
        <HStack gap={4}>
          <Text fontSize="sm" color="gray.600">
            File dichiara:
          </Text>
          <Text fontSize="sm" fontWeight="semibold">
            {detectedTeamFromFile}
          </Text>
        </HStack>
        <HStack gap={4}>
          <Text fontSize="sm" color="gray.600">
            Context corrente:
          </Text>
          <Text fontSize="sm" fontWeight="semibold">
            {currentTeamName}
          </Text>
        </HStack>

        <VStack align="stretch" gap={2}>
          <label
            style={{ display: 'flex', gap: 8, alignItems: 'center' }}
          >
            <input
              type="radio"
              name="m6-2-cross-team"
              value="current"
              checked={mode === 'current'}
              onChange={() => setMode('current')}
            />
            <Text fontSize="sm">
              Salva in <b>{currentTeamName}</b> (team corrente)
            </Text>
          </label>

          <label
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              opacity: detectedTeam ? 1 : 0.5,
            }}
            title={
              !detectedTeam ? 'Non sei coach di questo team' : undefined
            }
          >
            <input
              type="radio"
              name="m6-2-cross-team"
              value="detected"
              checked={mode === 'detected'}
              disabled={!detectedTeam}
              onChange={() => setMode('detected')}
            />
            <Text fontSize="sm">
              Cambia contesto a <b>{detectedTeamFromFile}</b> (rilevato dal
              file)
            </Text>
          </label>

          <label
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              opacity: otherTeams.length > 0 ? 1 : 0.5,
            }}
          >
            <input
              type="radio"
              name="m6-2-cross-team"
              value="other"
              checked={mode === 'other'}
              disabled={otherTeams.length === 0}
              onChange={() => setMode('other')}
            />
            <Text fontSize="sm">Salva in altro team di cui sono coach</Text>
          </label>
        </VStack>

        {mode === 'other' && otherTeams.length > 0 && (
          <select
            value={selectedTargetTeamId}
            onChange={(e) => onChangeTargetTeam(e.target.value)}
            style={{
              padding: 8,
              border: '1px solid #cbd5e1',
              borderRadius: 6,
              fontSize: 14,
            }}
          >
            <option value="">Seleziona team</option>
            {otherTeams.map((t) => (
              <option key={t._id} value={t._id}>
                {t.name}
              </option>
            ))}
          </select>
        )}
      </VStack>
    </Box>
  );
}
